/**
 * QUIPWA — note → flashcard bridge
 *
 * Creates flashcards from notes that the FSRS scheduler actually sees.
 *
 * Previously a card made from a note went only into `cards_index.json`, with a
 * placeholder `fsrs_state: { status: 'new' }` and a slugified string as its
 * `deck_id`. It never got a `review_state` row and never belonged to a real
 * `deck_instance`, so it never appeared in a session, never counted as due, and
 * was never scheduled. The card looked created but could not be studied — which
 * broke the product's core promise that notes and review are one flow.
 *
 * Here a note-derived card becomes indistinguishable from one imported via
 * .wflash: real deck_instance, real collection.db content, real review_state.
 *
 * Every write is local and authoritative; the deck's upload is queued in the
 * outbox (lib/sync/deck-uploads.ts). The card is reviewable the moment this
 * resolves, even fully offline, and reaches Drive on the next sync. (The old
 * best-effort upload was never retried when it failed.)
 */

import { getCollection } from '../db/collection-db-service.ts'
import type { StateDBService } from '../db/state-db-service.ts'
import type { DeckUploads } from '../sync/deck-uploads.ts'

export interface AddCardFromNoteInput {
  stateService: StateDBService
  /** Queues the deck's upload to Drive. */
  uploads: Pick<DeckUploads, 'queue'>
  /** Target deck. Matched case-insensitively against existing decks; created if absent. */
  deckName: string
  front: string
  back: string
}

export interface AddCardFromNoteResult {
  deckInstanceId: string
  cardId: string
  /** True when this call had to create the deck rather than reuse one. */
  createdDeck: boolean
}

function slugifyDeckId(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'notes_deck'
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

/**
 * Add a flashcard to a real, FSRS-scheduled deck, creating the deck if needed.
 *
 * Never waits for Drive: losing connectivity must not cost the user the card
 * they just wrote.
 */
export async function addCardFromNote({
  stateService,
  uploads,
  deckName,
  front,
  back,
}: AddCardFromNoteInput): Promise<AddCardFromNoteResult> {
  const trimmedName = deckName.trim()
  if (!trimmedName) throw new Error('El mazo necesita un nombre')

  // ── 1. Find or create the deck_instance ────────────────────────────────────
  const existing = await stateService.getDeckInstances()
  let instance = existing.find(
    (d) => d.deck_name.trim().toLowerCase() === trimmedName.toLowerCase()
  ) ?? null
  const createdDeck = instance === null

  if (!instance) {
    const ts = nowSeconds()
    instance = await stateService.insertDeckInstance({
      deck_id: slugifyDeckId(trimmedName),
      deck_name: trimmedName,
      wflash_drive_path: `${trimmedName}.wflash`,
      // Set by the queued upload; empty means "local only for now".
      drive_file_id: '',
      drive_modified_at: ts,
      imported_at: ts,
      card_count: 0,
    })
  }

  // ── 2. Write the card content into the deck's collection.db ────────────────
  const collection = await getCollection(instance.id)
  const cardId = await collection.createCard(instance.deck_id, front.trim(), back.trim())
  const cardCount = await collection.getCardCount()

  // ── 3. The step that was missing: make FSRS aware of it ────────────────────
  await stateService.insertInitialReviewState(cardId, instance.id, nowSeconds())
  await stateService.updateCardCount(instance.id, cardCount)
  await collection.flush()
  await stateService.flushNow?.()

  // ── 4. Queue the deck's upload (outbox) ─────────────────────────────────────
  await uploads.queue(instance.id)

  return { deckInstanceId: instance.id, cardId, createdDeck }
}
